import { useCallback, useMemo } from 'react';
import { useSearchParams, NavigateOptions } from 'react-router-dom';
import { QueryParams } from 'src/types';
import { sanitizeOutgoingContent } from 'src/utils';

// (olha): attn: https://github.com/remix-run/react-router/issues/9757
// useSearchParams has one issue. So, we use new URLSearchParams(window.location.search);

export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const typedSearchParams = useMemo(() => {
    const parsedParams: Partial<QueryParams> = {};
    searchParams.forEach((value, key) => {
      parsedParams[key as keyof QueryParams] = sanitizeOutgoingContent(value);
    });
    return parsedParams;
  }, [searchParams]);

  const setTypedSearchParams = useCallback(
    (newParams: Partial<QueryParams>, navigateOpts?: NavigateOptions) => {
      const updatedParams = new URLSearchParams(window.location.search);

      Object.keys(newParams).forEach((key) => {
        const value = newParams[key as keyof QueryParams];
        if (value !== undefined) {
          updatedParams.set(key, sanitizeOutgoingContent(value));
        }
      });

      setSearchParams(updatedParams, navigateOpts);
    },
    [setSearchParams],
  );

  const arraySearchParams = useMemo(() => {
    const result: Partial<Record<keyof QueryParams, string[]>> = {};
    const keyValuesMap = new Map<string, string[]>();

    searchParams.forEach((value, key) => {
      if (!keyValuesMap.has(key)) {
        keyValuesMap.set(key, []);
      }

      keyValuesMap.get(key)?.push(sanitizeOutgoingContent(value));
    });

    keyValuesMap.forEach((values, key) => {
      if (values.length > 1) {
        result[key as keyof QueryParams] = values;
      }
    });

    return result;
  }, [searchParams]);

  const appendToSearchParamArray = (
    key: keyof QueryParams,
    value: string,
    navigateOpts?: NavigateOptions,
  ) => {
    const updatedParams = new URLSearchParams(window.location.search);
    const existingValues = updatedParams.getAll(key as string);

    if (!existingValues.includes(value)) {
      updatedParams.append(key as string, sanitizeOutgoingContent(value));
      setSearchParams(updatedParams, navigateOpts);
    }
  };

  const removeSearchParam = useCallback(
    (paramName: keyof QueryParams, navigateOpts?: NavigateOptions) => {
      const newSearchParams = new URLSearchParams(window.location.search);
      newSearchParams.delete(paramName);
      setSearchParams(newSearchParams, navigateOpts);
    },
    [setSearchParams],
  );

  const removeMultipleSearchParams = useCallback(
    (paramNames: (keyof QueryParams)[], navigateOpts?: NavigateOptions) => {
      const newSearchParams = new URLSearchParams(window.location.search);

      for (const param of paramNames) {
        newSearchParams.delete(param);
      }

      setSearchParams(newSearchParams, navigateOpts);
    },
    [setSearchParams],
  );

  const removeAllSearchParams = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  return {
    arraySearchParams,
    searchParams: typedSearchParams,
    setSearchParams: setTypedSearchParams,
    appendToSearchParamArray,
    removeSearchParam,
    removeAllSearchParams,
    removeMultipleSearchParams,
  };
};
